<script setup lang="ts">
import { computed } from "vue";

import Notifications from "../Utils/Notifications.vue";
import LoadingSpinner from "../Loading/LoadingSpinner.vue";

const props = withDefaults(
  defineProps<{
    cursor?: "pointer" | "default" | "not-allowed";
    icon: string;
    iconClass?: string;
    notifications?: number;
    disabled?: boolean;
    label?: string;
    color: "primary" | "button" | "destructive" | "super-cta" | "disabled" | "default";
    size?: "sm" | "md" | "lg";
    underline?: boolean;
    isLoading?: boolean;
    iconPosition?: "left" | "right";
    id?: string;
  }>(),
  {
    cursor: "pointer",
    iconPosition: "left",
    size: "md",
  }
);

const focusClass = computed(() => {
  if (props.color === "destructive") {
    return "focus-visible:outline focus-visible:outline-2 focus-visible:outline-app-red-500 focus-visible:outline-offset-2";
  }
  return "focus-ring";
});

const sizeClass = computed(() => {
  if (props.size === "sm") return "h-8 min-w-[32px] px-2 t-caption";
  if (props.size === "lg") return "h-12 min-w-[48px] px-4 t-body";
  return "h-10 min-w-[40px] px-3 t-small"; // md (default)
});

const colorClass = computed(() => {
  if (props.color === "super-cta")
    return "bg-app-green-600 dark:bg-app-green-fill text-white hover:bg-app-green-700 dark:hover:bg-app-green-fill-hover";
  if (props.color === "button") return "bg-slate-200 text-type-default hover:bg-slate-300";
  if (props.color === "primary")
    return "bg-app-blue-600 dark:bg-app-blue-fill text-white hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover";
  if (props.color === "destructive")
    return "bg-app-red-600 dark:bg-app-red-fill text-white hover:bg-app-red-700 dark:hover:bg-app-red-fill-hover";
  if (props.color === "disabled") return "bg-slate-200 opacity-50 text-slate-600 cursor-default!";
  if (props.color === "default") return "bg-slate-200 text-type-default hover:bg-slate-300";
  return "";
});
</script>

<template>
  <!-- style kept: cursor is an arbitrary dynamic value passed as a prop -->
  <button
    :id="props.id"
    :disabled="disabled"
    :aria-label="label || icon"
    class="rounded-8 relative z-10 flex shrink-0 items-center justify-center leading-none font-bold whitespace-nowrap shadow-xs transition-all"
    :class="[colorClass, sizeClass, focusClass]"
    :style="{ cursor: cursor }">
    <Notifications :notifications="notifications ? notifications : 0" />
    <div
      class="pointer-events-none"
      :class="[label ? 'flex gap-2' : '']">
      <template v-if="iconPosition === 'left'">
        <LoadingSpinner
          v-if="isLoading"
          size="xs"
          :fill="props.color" />
        <!-- style kept: cursor is an arbitrary dynamic value passed as a prop -->
        <font-awesome-icon
          v-else
          :icon="icon"
          :class="[iconClass]"
          :style="{ cursor: cursor }" />
      </template>
      <span
        v-if="label"
        :class="[underline ? 'underline' : '']"
        >{{ label }}</span
      >
      <template v-if="iconPosition === 'right'">
        <LoadingSpinner
          v-if="isLoading"
          size="xs"
          :fill="props.color" />
        <!-- style kept: cursor is an arbitrary dynamic value passed as a prop -->
        <font-awesome-icon
          v-else
          :icon="icon"
          :class="[iconClass]"
          :style="{ cursor: cursor }" />
      </template>
    </div>
  </button>
</template>
